import React, { useEffect, useState, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  BookOpenIcon, 
  ShieldCheckIcon,
  XMarkIcon,
  Bars3Icon,
  LanguageIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '@/i18n/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { locales } from '@/i18n/i18n';
import AvatarDropdown from '@/components/ui/AvatarDropdown';
import UserConfigModal from '@/components/ui/UserConfigModal';
import ChangePasswordModal from '@/components/ui/ChangePasswordModal';

// Utility function for class name merging
const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

const MainLayout = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { t, locale, setLocale } = useLanguage();
  const { user } = useAuth();
  const languageMenuRef = useRef(null);
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isUserConfigModalOpen, setIsUserConfigModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

  // Không cần kiểm tra loading state nữa vì đã được xử lý bởi PrivateRoute
  // User data đã được load từ localStorage

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setIsLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    {
      title: 'Sách',
      href: '/books/list',
      icon: <BookOpenIcon className="h-5 w-5" />,
    },
    {
      title: t('app.nav.admin'),
      href: '/admin',
      icon: <ShieldCheckIcon className="h-5 w-5" />,
    },
  ];


  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const languageNames = {
    en: 'English',
    vi: 'Tiếng Việt'
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg px-4 pb-0">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl font-bold">{t('app.name')}</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = pathname.startsWith(item.href);
                return (
                  <li key={item.href}>
                    <Link 
                      to={item.href}
                      className={cn(
                        "flex h-10 items-center gap-2 px-4 text-sm font-medium transition-colors border-b-2",
                        isActive 
                          ? "border-white" 
                          : "border-transparent"
                      )}
                    >
                      {item.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 rounded-md text-white hover:bg-white/10"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Language Switcher */}
          <div className="relative" ref={languageMenuRef}>
            <button
              onClick={toggleLanguageMenu}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-white hover:bg-white/10 transition-colors"
            >
              <LanguageIcon className="h-4 w-4" />
              <span className="text-sm">{languageNames[locale]}</span>
            </button>
            
            {isLanguageMenuOpen && (
              <div className="absolute right-0 mt-1 py-1 bg-white shadow-lg rounded-md min-w-36 z-20">
                {locales.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => {
                      setLocale(lang);
                      setIsLanguageMenuOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm ${
                      locale === lang 
                        ? 'bg-blue-50 text-blue-700 font-medium' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {languageNames[lang] || lang}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            {user ? (
              <div className="flex items-center gap-2">
                <span className="hidden md:inline-block">{user.name}</span>
                <AvatarDropdown
                  onConfigClick={() => setIsUserConfigModalOpen(true)}
                  onChangePasswordClick={() => setIsChangePasswordModalOpen(true)}
                />
              </div>
            ) : (
              <Link 
                to="/auth/signin"
                className="rounded-md bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20"
              >
                Đăng nhập
              </Link>
            )}
          </div>
          
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-b shadow-lg">
          <nav className="px-4 py-2">
            <ul className="space-y-1">
              {navItems.map((item) => {
                const isActive = pathname.startsWith(item.href);
                return (
                  <li key={item.href}>
                    <Link 
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                        isActive 
                          ? "bg-blue-50 text-blue-700" 
                          : "text-gray-700 hover:bg-gray-100"
                      )}
                    >
                      {item.icon}
                      {item.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
            
            {/* Mobile Language Switcher */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                Language / Ngôn ngữ
              </div>
              <div className="space-y-1">
                {locales.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => {
                      setLocale(lang);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      locale === lang 
                        ? 'bg-blue-50 text-blue-700 font-medium' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {languageNames[lang] || lang}
                  </button>
                ))}
              </div>
            </div>
          </nav>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      {/* User Configuration Modal */}
      <UserConfigModal 
        isOpen={isUserConfigModalOpen} 
        onClose={() => setIsUserConfigModalOpen(false)} 
      />
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </div>
  );
};

export default MainLayout; 